import { Command } from 'cmdk';
import React, { useMemo, useCallback } from 'react';
import {
  TextField,
  Box,
  ModalPrimitiveDialog,
  ModalPrimitiveOverlay,
  ModalPrimitive,
  EmptyStateDescription,
  EmptyState,
  EmptyStateTitle,
  Icon,
  Text,
  Flex,
  FormattedDateTime,
  EmptyStateGraphic,
  Spinner,
  LabelIcon,
  Divider,
  InlineStack,
} from '@customerio/pluma-components/react';
import { servicesQuery } from '@/services/api';
import { contextualParams } from '@/services/api/ts-rest';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import { useCurrentWorkspace } from '@/context/current-workspace';
import * as styles from './index.css';
import type { SearchResult } from '@/services/auth/types';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { createModal } from '@/hooks/system/create-modal';
import { LABEL } from './constants';
import { getIconNameFromSearchResult, getHeadingbyKind } from './utils';

export const useCommandDialog = createModal('command-dialog', {
  type: 'ephemeral',
});

export function CommandDialog() {
  const safeNavigate = useSafeNavigate();
  const commandDialog = useCommandDialog();
  const [inputValue, setInputValue] = React.useState('');
  const [debouncedInputValue] = useDebouncedValue(inputValue, 250);
  const currentWorkspace = useCurrentWorkspace();
  const { workspaceId } = contextualParams();

  const { data, isLoading, isError } = servicesQuery.search.useQuery({
    queryKey: ['search', debouncedInputValue, workspaceId],
    queryData: {
      params: { workspaceId: workspaceId! },
      query: { q: debouncedInputValue },
    },
    enabled: commandDialog.isOpen && debouncedInputValue.length > 0 && workspaceId != null,
  });

  // Toggle the menu when ⌘K is pressed
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        commandDialog.toggle();
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [commandDialog]);

  const groupedResults = useMemo(() => {
    if (data == null) {
      return [] as [SearchResult['kind'], SearchResult[]][];
    }

    return Object.entries(
      data.body.results.reduce((groups, item) => {
        const kind = item.kind;
        groups[kind] ??= [];
        groups[kind]?.push(item);
        return groups;
      }, {} as Record<SearchResult['kind'], typeof data.body.results>),
    );
  }, [data]) as [SearchResult['kind'], SearchResult[]][];

  const handleClose = useCallback(() => {
    commandDialog.close();
    setInputValue('');
  }, [commandDialog]);

  const handleItemSelect = useCallback(
    (url: string) => {
      safeNavigate(url);
      handleClose();
    },
    [safeNavigate, handleClose],
  );

  const hasNoResults = data?.body.results != null && data.body.results.length === 0;

  return (
    <ModalPrimitive
      initialFocusIndex={-1}
      animationTransitionDuration={125}
      isOpen={commandDialog.isOpen}
      onClose={handleClose}
    >
      <ModalPrimitiveOverlay>
        <ModalPrimitiveDialog className={styles.dialog}>
          <Command loop label={LABEL} shouldFilter={false}>
            <Command.Input asChild value={inputValue} onValueChange={setInputValue}>
              <TextField
                autoFocus={true}
                placeholder={LABEL}
                onInput={(e) => setInputValue(e.target.value)}
                ariaLabel="Use the command palette to search for campaigns, templates, segments, or anything else in this workspace."
                className={`${styles.largeTextField} fs-mask`}
              />
            </Command.Input>
            <Command.List>
              {isLoading ? (
                <Command.Loading asChild>
                  <EmptyState>
                    <EmptyStateGraphic>
                      <Spinner className={styles.spinner} color="information" />
                    </EmptyStateGraphic>
                    <EmptyStateTitle>
                      Searching for "<span className="fs-mask">{debouncedInputValue}</span>"
                    </EmptyStateTitle>
                    <EmptyStateDescription>
                      Hang on while we search in {currentWorkspace?.name ?? 'this workspace'}
                    </EmptyStateDescription>
                  </EmptyState>
                </Command.Loading>
              ) : null}
              {debouncedInputValue.length === 0 ? (
                <Command.Empty asChild>
                  <EmptyState>
                    <EmptyStateTitle icon="search">Search {currentWorkspace?.name ?? 'Workspace'}</EmptyStateTitle>
                    <EmptyStateDescription>
                      Try searching for campaigns, templates, segments, or anything else in this workspace.
                    </EmptyStateDescription>
                  </EmptyState>
                </Command.Empty>
              ) : null}
              {hasNoResults || isError ? (
                <Command.Empty asChild>
                  <EmptyState>
                    <EmptyStateTitle icon="search">
                      No results found for <span className="fs-mask">{debouncedInputValue}</span>
                    </EmptyStateTitle>
                    <EmptyStateDescription>Try adjusting your search to find matches.</EmptyStateDescription>
                  </EmptyState>
                </Command.Empty>
              ) : null}
              {data?.body.results != null ? (
                <>
                  {groupedResults.map(([kind, items]) => (
                    <Command.Group key={kind} heading={getHeadingbyKind(kind)}>
                      {items.map((item, index) => {
                        const safeUrlValue = new URL(item.url).pathname;
                        const hasDescription = item.kind === 'campaign' || item.kind === 'segment';
                        const key = `${item.kind}-${index}-${item.id ?? encodeURIComponent(item.url)}`;

                        return (
                          <Command.Item onSelect={() => handleItemSelect(safeUrlValue)} key={key} value={key} asChild>
                            <Box alignItems="flex-start" gap="100" display="flex">
                              <Icon mt="025" name={getIconNameFromSearchResult(item)} />
                              <Flex direction="column" gap="025">
                                <Flex direction="column">
                                  <Text weight="semibold" className="fs-mask">
                                    {item.title}
                                  </Text>
                                  {hasDescription && item.other?.description != null ? (
                                    <Text size="s" color="subtle" className="fs-mask">
                                      {item.other.description}
                                    </Text>
                                  ) : null}
                                </Flex>
                                {item.other?.created_at != null && item.other.created_at != null ? (
                                  <>
                                    <Text size="xs" color="subtle">
                                      Created at{' '}
                                      <FormattedDateTime
                                        className="fs-mask"
                                        date={item.other.created_at}
                                        format="medium"
                                      />
                                    </Text>
                                  </>
                                ) : null}
                              </Flex>
                            </Box>
                          </Command.Item>
                        );
                      })}
                    </Command.Group>
                  ))}
                </>
              ) : null}
            </Command.List>
          </Command>
          <Divider />
          <InlineStack w="full" alignInline="space-between" p="100">
            <Flex gap="050" alignItems="center">
              {/* TODO either label with ESC or get an ESC icon */}
              <LabelIcon name="arrow-left" shape="square" label="ESC key" />
              <Text size="xs" color="subtle">
                Close
              </Text>
            </Flex>

            <InlineStack alignInline="space-between" gap="250">
              <Flex gap="050" alignItems="center">
                <LabelIcon name="arrow-up" shape="square" label="Arrow keys key" />
                <LabelIcon name="arrow-down" shape="square" label="Arrow keys key" />
                <Text size="xs" color="subtle">
                  Navigate
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <LabelIcon name="arrow-right" shape="square" label="Enter key" />
                <Text size="xs" color="subtle">
                  Select
                </Text>
              </Flex>
            </InlineStack>
          </InlineStack>
        </ModalPrimitiveDialog>
      </ModalPrimitiveOverlay>
    </ModalPrimitive>
  );
}
